/* eslint @typescript-eslint/prefer-nullish-coalescing: 1 -- Do not want to break existing code by switching to nullish coalesce blidnly */
import type { Variables } from 'graphql-request';
import { gql, request } from 'graphql-request';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

import { getGraphQlEndpoint } from '@/api/backendAPI';
import queryClient from '@/api/queryClient';
import { getCartIdCookie, saveCartIdCookie } from '@/utils/cartUtils';

import { logRequestError } from '../logger/logToDatadog';
import type {
  RefreshCart,
  RefreshCart_cart_refreshCart_cart,
  RefreshCart_cart_refreshCart_cart_lineItems,
  RefreshCartVariables,
} from './types/RefreshCart';

export type RefreshCartResponse = RefreshCart_cart_refreshCart_cart;
export type CartLineItem = RefreshCart_cart_refreshCart_cart_lineItems;

export const query = gql`
  query RefreshCart($cartId: String) {
    cart(cartId: $cartId) {
      refreshCart {
        resultCategory
        cartId
        cartIdChangeReason
        cart {
          promotions {
            promotionCode
            campaign
          }
          lineItems {
            skuCode
            quantity
          }
          isWarrantyReplacement
          itemCountForIcon
          maxRingQuantity
          maxChargerQuantity
          ringCount
          chargerCount
        }
      }
    }
  }
`;

function withQueryKey(req: RefreshCartVariables) {
  return {
    key: ['refreshCart', req.cartId],
    fetch: async (): Promise<RefreshCartResponse | undefined | null> => {
      try {
        const url = getGraphQlEndpoint()!;
        const data: RefreshCart | undefined = await request(
          url,
          query,
          req as Variables,
        );

        const refreshResponse = data?.cart?.refreshCart;

        if (refreshResponse?.resultCategory === 'success') {
          // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
          saveCartIdCookie(refreshResponse.cartId as string);
        }

        return refreshResponse?.cart;
      } catch (err) {
        void logRequestError('cart', 'RefreshCart', err as any, req);
        throw err;
      }
    },
  };
}

export async function fetchRefreshCart() {
  const cartId = getCartIdCookie();
  const { key, fetch } = withQueryKey({ cartId });
  return queryClient.fetchQuery(key, fetch);
}

export async function fetchCartDiscountCodes() {
  const cart = await fetchRefreshCart();

  return (cart?.promotions || []).map((promotion) => promotion?.promotionCode);
}

export function useRefreshCart(): UseQueryResult<RefreshCartResponse> {
  const cartId = getCartIdCookie();
  const { key, fetch } = withQueryKey({ cartId });
  return useQuery(key, fetch, {
    // Keep data cached for 5 minutes
    cacheTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });
}
